/**
 * Reusable part / quantity picker grid.
 *
 * A collapsible table for choosing per-item quantities — the recurring
 * "Item # / Name / Description / Qty" widget used wherever a user assembles
 * a small list of catalog parts with counts (RMA inventory adjustments,
 * packing supplies, and similar). Presentational only: the caller supplies
 * the rows and owns the selected-quantity state; this component renders the
 * grid, the per-row quantity dropdowns, and a collapsed summary.
 *
 * Contains:
 * - PartQuantityGrid — default export
 */

import { Box, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

const QTY_CHOICES_SMALL = [0, 1, 2, 3, 4, 5];

// Column-header styling — mirrors the overline section headers used across
// the theme-new surfaces (muted, uppercase) instead of a solid color bar.
const HEADER_CELL_SX = {
  color: 'text.secondary',
  fontWeight: 600,
  fontSize: '0.7rem',
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  bgcolor: 'background.default',
};

/**
 * @param {object} props
 * @param {string} props.title                 Section heading
 * @param {Array<{id:(number|string), partNumber?:string, name?:string, description?:string}>} props.items
 * @param {(id, qty) => void} props.onQtyChange
 * @param {(id) => number} props.getQty         Resolve the current qty for a row id
 * @param {number[]} [props.qtyChoices]
 * @param {boolean} [props.defaultOpen]
 * @param {boolean} [props.loading]
 * @param {() => void} [props.onExpand]         Fired when the user opens the grid (lazy load hook)
 * @param {{partNumber?:string, name?:string, description?:string}} [props.columnLabels]
 * @param {string} [props.emptyLabel]
 */
export default function PartQuantityGrid({
  title,
  items,
  onQtyChange,
  getQty,
  qtyChoices = QTY_CHOICES_SMALL,
  defaultOpen = false,
  loading = false,
  onExpand,
  columnLabels,
  emptyLabel = 'No items selected',
}) {
  const [open, setOpen] = useState(defaultOpen);

  const labels = {
    partNumber: 'Item #',
    name: 'Name',
    description: 'Description',
    ...columnLabels,
  };

  const handleExpand = () => {
    setOpen(true);
    onExpand?.();
  };

  const selected = items.filter(item => getQty(item.id) > 0);
  const hasSelection = selected.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="body2" fontWeight="bold">
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'primary.main', cursor: 'pointer' }}
          onClick={() => (open ? setOpen(false) : handleExpand())}
        >
          {open ? '(close)' : hasSelection ? '(change)' : '(add)'}
        </Typography>
      </Box>

      {open ? (
        loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 'var(--radius-md)', overflow: 'hidden', mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEADER_CELL_SX}>{labels.partNumber}</TableCell>
                  <TableCell sx={HEADER_CELL_SX}>{labels.name}</TableCell>
                  <TableCell sx={HEADER_CELL_SX}>{labels.description}</TableCell>
                  <TableCell sx={{ ...HEADER_CELL_SX, width: 72 }}>Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => (
                  <TableRow
                    key={item.id}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell>{item.partNumber || ''}</TableCell>
                    <TableCell>{item.name || ''}</TableCell>
                    <TableCell>{item.description || ''}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        hiddenLabel
                        value={getQty(item.id)}
                        onChange={e => onQtyChange(item.id, e.target.value)}
                        sx={{ minWidth: 60 }}
                      >
                        {qtyChoices.map(n => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )
      ) : hasSelection ? (
        <Box component="ul" sx={{ pl: 2, mt: 0 }}>
          {selected.map(item => (
            <li key={item.id}>
              {item.partNumber || ''} - {item.name || ''} - Qty: {getQty(item.id)}
            </li>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      )}
    </Box>
  );
}

PartQuantityGrid.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onQtyChange: PropTypes.func.isRequired,
  getQty: PropTypes.func.isRequired,
  qtyChoices: PropTypes.arrayOf(PropTypes.number),
  defaultOpen: PropTypes.bool,
  loading: PropTypes.bool,
  onExpand: PropTypes.func,
  columnLabels: PropTypes.object,
  emptyLabel: PropTypes.string,
};
